import type { AuthSessionData } from '@/auth/AuthSession';
import type { BiometricKind } from './biometricDevice';

import { deleteSecret } from './biometricDevice';

/**
 * The non-secret half of a biometric enrollment, readable without a prompt so the login screen can
 * offer "Sign in with Face ID" for the right account. The secret itself stays in the Keychain/Keystore.
 */
export type BiometricEnrollment = {
	credentialId: string;
	userId: number;
	kind: BiometricKind;
	email?: string;
	loginMethod?: AuthSessionData['loginMethod'];
	lockEnabled: boolean;
};

const ENROLLMENT_KEY = 'OnTheBuckBiometric';
const OFFER_DISMISSED_KEY = 'OnTheBuckBiometricOfferDismissed';

let offerPending = false;

export function getEnrollment(): BiometricEnrollment | null {
	try {
		const raw = localStorage.getItem(ENROLLMENT_KEY);
		return raw ? (JSON.parse(raw) as BiometricEnrollment) : null;
	} catch {
		return null;
	}
}

export function saveEnrollment(enrollment: BiometricEnrollment): void {
	localStorage.setItem(ENROLLMENT_KEY, JSON.stringify(enrollment));
}

export function clearEnrollment(): void {
	localStorage.removeItem(ENROLLMENT_KEY);
}

/** A different account on this device must not inherit Face ID access to the previous one. */
export function forgetOtherAccountsEnrollment(userId: number): void {
	const enrollment = getEnrollment();
	if (enrollment && enrollment.userId !== userId) {
		clearEnrollment();
		void deleteSecret();
	}
}

/** Runs after every password or Google sign-in. */
export function noteNonBiometricSignIn(userId: number, { offerBiometrics }: { offerBiometrics: boolean }): void {
	forgetOtherAccountsEnrollment(userId);
	offerPending = offerBiometrics;
}

/** True once per sign-in, so the "Enable Face ID?" offer cannot show twice. */
export function consumeBiometricOffer(): boolean {
	const pending = offerPending;
	offerPending = false;
	return pending;
}

function dismissedOfferUserIds(): number[] {
	try {
		const ids: unknown = JSON.parse(localStorage.getItem(OFFER_DISMISSED_KEY) ?? '[]');
		return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : [];
	} catch {
		return [];
	}
}

export function isBiometricOfferDismissed(userId: number): boolean {
	return dismissedOfferUserIds().includes(userId);
}

/**
 * Stops the post-sign-in offer for this account on this device. Kept per account, so turning it
 * down as one person does not silence the offer for someone else who signs in here.
 */
export function dismissBiometricOffer(userId: number): void {
	const ids = dismissedOfferUserIds();
	if (!ids.includes(userId)) {
		localStorage.setItem(OFFER_DISMISSED_KEY, JSON.stringify([...ids, userId]));
	}
}
